import fetch from 'isomorphic-fetch'
import { RSAA, RSAACall, RSAAAction } from 'redux-api-middleware'

import { trackNewVehicles } from '@cinch/oxd-utils/analytics/src/product-display'
import { Env, getEnv } from '@cinch/oxd-utils/env/src'
import { dealerVehicleSummaryFormatter } from '@cinch/oxd-utils/form/src'
import { queryString } from '@cinch/oxd-utils/requests/src'

import { types } from '.'

const baseUrl = getEnv(Env.VehicleServiceUrl)

export const updateForm = (payload: Cinch.FilterFormData) => ({ type: types.DEALER_VEHICLE_FORM_UPDATE, payload })

export const getListings = ({ query, ...params }: Cinch.FilterFormData, replace = false) => ({
  [RSAA]: {
    endpoint: `${baseUrl}/vehicles?${queryString(params)}`,
    method: 'GET',
    fetch,
    types: [
      types.DEALER_VEHICLE_LISTING_REQUEST,
      {
        type: types.DEALER_VEHICLE_LISTING_SUCCESS,
        meta: { params: { query: query || '', ...params }, replace },
        payload: async (_action, _state, response): Promise<Cinch.DealerVehicleListingResponse> => {
          const res = (await response.json()) as Cinch.DealerVehicleListingResponse
          res.vehicleListings = res.vehicleListings.map(dealerVehicleSummaryFormatter)

          trackNewVehicles(res.vehicleListings)

          return res
        },
      },
      types.DEALER_VEHICLE_LISTING_FAILURE,
    ],
  } as RSAACall<any, (_action: RSAAAction, _state: any, response: Response) => void, any>,
})

export const getSummary = (
  vehicleId: number,
  latitude?: number | string | null,
  longitude?: number | string | null,
) => {
  const base = `${baseUrl}/summaries/${vehicleId}`
  const endpoint = !!latitude && !!longitude ? `${base}?${queryString({ latitude, longitude })}` : base

  return {
    [RSAA]: {
      endpoint,
      method: 'GET',
      fetch,
      types: [
        {
          type: types.DEALER_VEHICLE_SUMMARY_REQUEST,
          meta: { vehicleId },
        },
        {
          type: types.DEALER_VEHICLE_SUMMARY_SUCCESS,
          payload: async (_action, _state, response) => {
            const res = await response.json()

            return dealerVehicleSummaryFormatter(res)
          },
        },
        {
          type: types.DEALER_VEHICLE_SUMMARY_FAILURE,
          payload: async (_action, _state, response) => {
            const res = (await response.json()) as Cinch.ProblemDetail & { name: string; message: string }

            return res.detail || res.message || ''
          },
          meta: { vehicleId },
        },
      ],
    } as RSAACall<any, (_action: RSAAAction, _state: any, response: Response) => void, any>,
  }
}

export const search = (query: string) => ({
  [RSAA]: {
    endpoint: `${baseUrl}/vehicles/search?${queryString({ searchText: query })}`,
    method: 'GET',
    fetch,
    headers: () => ({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    }),
    types: [
      types.DEALER_VEHICLE_SEARCH_REQUEST,
      {
        type: types.DEALER_VEHICLE_SEARCH_SUCCESS,
        payload: async (_action, _state, response): Promise<Partial<Cinch.FilterFormData>> => {
          const { searchFilters } = (await response.json()) as Cinch.VehicleSearchResponse

          const formatFilters = (obj: Cinch.FilterFormData): Cinch.FilterFormData => {
            const output = obj as { [key: string]: any }
            const lowercaseProperties = ['doors', 'bodyType', 'colour', 'transmissionType', 'fuelType']

            lowercaseProperties.forEach((prop) => {
              output[prop] = (output[prop] && output[prop].toLowerCase()) || ''
            })

            // Here we need to make sure we set any nulls that the filter initial values expect as empty strings are converted
            const emptyProps: Array<keyof Cinch.FilterFormData> = [
              'longitude',
              'latitude',
              'bodyType',
              'colour',
              'doors',
              'fuelType',
              'keywords',
              'make',
              'selectedModel',
              'transmissionType',
              'query',
            ]

            emptyProps.forEach((prop) => {
              if (output[prop] === null) {
                output[prop] = '' as any
              }
            })

            return output
          }

          return formatFilters({ ...searchFilters, query })
        },
      },
      types.DEALER_VEHICLE_SEARCH_FAILURE,
    ],
  } as RSAACall<any, (_action: RSAAAction, _state: any, response: Response) => void, any>,
})

export const getAllSummaries = (vehicleIds: number[]) => {
  const endpoint = `${baseUrl}/summaries/?vehicleIds=${vehicleIds.join(',')}`

  return {
    [RSAA]: {
      endpoint,
      method: 'GET',
      fetch,
      types: [
        {
          type: types.DEALER_VEHICLE_SUMMARY_ALL_REQUEST,
          meta: { vehicleIds },
        },
        {
          type: types.DEALER_VEHICLE_SUMMARY_ALL_SUCCESS,
          meta: { vehicleIds },
          payload: async (_action, _state, response) => {
            const res: Array<Cinch.DealerVehicleSummaryResponse | null> = await response.json()
            const summaries = res.map((summary) => (summary ? dealerVehicleSummaryFormatter(summary) : null))

            return summaries
          },
        },
        {
          type: types.DEALER_VEHICLE_SUMMARY_ALL_FAILURE,
          meta: { vehicleIds },
        },
      ],
    } as RSAACall<any, (_action: RSAAAction, _state: any, response: Response) => void, any>,
  }
}
